import { useQuery } from '@tanstack/react-query';
import { Form, useFormikContext } from 'formik';

import { ProductMessage } from '../../../../types/ProductMessage';
import { Button } from '../../../cdl/Button/Button';
import { DrawerContentLayout } from '../../../cdl/Drawer/DrawerContentLayout';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { FormikInput } from '../../../cdl/Input/FormikInput';
import { Text } from '../../../cdl/Text/Text';
import { queries } from '../../../common/api/queryKeys/queries';
import { DataSheet } from '../../../common/DataSheet/DataSheet';
import { FormikDebug } from '../../../common/form-elements/formik/FormikDebug';
import { FormikEnumComboBox } from '../../../common/form-elements/formik/FormikEnumComboBox';
import { formatDateTime } from '../../../common/helpers/formatDateTime.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { Box } from '../../../common/ui/Box';

import { ManageEquivalentsDrawer } from './ManageEquivalentsDrawer';

interface ProductBaseFormProps {
    product?: ProductMessage;
}

export interface ProductFormState {
    supplierProductGroup: string;
    name: string;
    group: string;
    packTypeDefault: string;
    unitDefault: string;
}

export const ProductBaseForm = (props: ProductBaseFormProps) => {
    const formikContext = useFormikContext();
    const role = useRole();
    const { push } = useDrawer();

    const successorProductId = props.product?.latestSuccessorProductId;
    const successorProductQuery = useQuery({
        ...queries.products.byId(successorProductId ? [successorProductId] : []),
        enabled: !!successorProductId,
    });
    const successorProduct = successorProductQuery.data?.[0];

    const handleManageEquivalents = () => {
        if (!props.product) return;
        push({
            header: { title: translate('productsOverview.equivalents.drawerTitle') },
            content: <ManageEquivalentsDrawer product={props.product} />,
            width: '40vw',
        });
    };

    return (
        <Form style={{ height: '100%', minHeight: 0 }}>
            <DrawerContentLayout
                isBusy={formikContext.isSubmitting}
                hasUnsavedChanges={formikContext.dirty}
                primaryAction={
                    <Button type="submit" emphasis="high" isLoading={formikContext.isSubmitting}>
                        {props.product
                            ? translate('productsOverview.updateProduct')
                            : translate('productsOverview.createProduct')}
                    </Button>
                }
            >
                <FormikInput name="name" label={translate('productsOverview.labels.name')} />
                <FormikEnumComboBox
                    name="supplierProductGroup"
                    enumType="supplierProductGroup"
                    label={translate('productsOverview.labels.supplier')}
                />
                <FormikEnumComboBox
                    name="group"
                    enumType="productGroup"
                    context="LUBES"
                    label={translate('productsOverview.labels.group')}
                />
                <FormikEnumComboBox
                    name="packTypeDefault"
                    enumType="packType"
                    label={translate('productsOverview.labels.packType')}
                />
                <FormikEnumComboBox
                    name="unitDefault"
                    enumType="productUnit"
                    context="LUBES"
                    label={translate('productsOverview.labels.unitDefault')}
                />

                {props.product ? (
                    <Box marginTop={16}>
                        <DataSheet>
                            {successorProduct ? (
                                <DataSheet.Row
                                    label={translate('productsOverview.labels.successor')}
                                    value={successorProduct.name}
                                />
                            ) : null}
                            <DataSheet.Row
                                label={translate('productsOverview.labels.dateCreated')}
                                value={formatDateTime({ date: props.product.dateCreated })}
                            />
                            <DataSheet.Row
                                label={translate('productsOverview.labels.lastEdit')}
                                value={formatDateTime({ date: props.product.dateUpdated })}
                            />
                        </DataSheet>
                    </Box>
                ) : null}

                {props.product && role.isSuperAdmin() ? (
                    <Box marginTop={16}>
                        <Text variant="subtitle1" as="h2">
                            {translate('productsOverview.equivalents.title')}
                        </Text>
                        <Box marginTop={4}>
                            <Text variant="small" color="foreground.subtle">
                                {translate('productsOverview.equivalents.description')}
                            </Text>
                        </Box>
                        <Box marginTop={12}>
                            <Button emphasis="medium" type="button" onClick={handleManageEquivalents}>
                                {translate('productsOverview.equivalents.manage')}
                            </Button>
                        </Box>
                    </Box>
                ) : null}
                <FormikDebug />
            </DrawerContentLayout>
        </Form>
    );
};
